import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface StockValuationRow {
  id: number;
  code: string;
  name: string;
  stock_qty: number;
  purchase_price: number;
  selling_price: number;
  value_at_cost: number;
  value_at_sell: number;
}

interface StockValuationTotals {
  total_at_cost: number;
  total_at_sell: number;
}

interface StockValuationData {
  rows: StockValuationRow[];
  totals: StockValuationTotals;
}

interface StockValuationResponse {
  data: StockValuationData;
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'code', header: 'Code' },
  { key: 'name', header: 'Name' },
  { key: 'stock_qty', header: 'Stock Qty' },
  { key: 'purchase_price', header: 'Purchase Price', money: true },
  { key: 'selling_price', header: 'Selling Price', money: true },
  { key: 'value_at_cost', header: 'Value at Cost', money: true },
  { key: 'value_at_sell', header: 'Value at Sell', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function StockValuationReport() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'stock-valuation'],
    queryFn: () => apiFetch<StockValuationResponse>('/api/reports/stock-valuation'),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];
  const totalsRow = report
    ? {
        name: 'Total',
        value_at_cost: report.totals.total_at_cost,
        value_at_sell: report.totals.total_at_sell,
      }
    : undefined;

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Stock Valuation</h1>
        <ExportButtons endpoint="/api/reports/stock-valuation" />
      </div>

      {/* Loading / error */}
      {isLoading && (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
      )}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && <ReportTable columns={COLUMNS} rows={rows} totalsRow={totalsRow} keyField="id" />}
    </div>
  );
}
